// packages
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Save, Sparkles, Trash2, Calendar, Activity, AlertTriangle, ListChecks, Loader2 } from 'lucide-react'

// components
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PageHeader } from '@/components/common/page-header'
import { MarkersTable } from '@/components/exam-analysis/markers-table'
import { ExamAnalysisResultDialog } from '@/components/exam-analysis/result-dialog'
import { computeMarkerStats, getMarkersForCategory, resolveRangeKey, buildMarkersContext } from '@/components/exam-analysis/marker-utils'

// libs
import { showConfirm } from '@/lib/alerts'

// hooks
import { useToast } from '@/hooks/use-toast'

// store
import { useExamAnalysisStore } from '@/store/exam-analysis'

// entities
import {
  ExamAnalysisFormSchema,
  examAnalysisFormSchema,
  validateExamAnalysisCrossFields,
  ExamCollection,
  ExamMarkerValues,
  exam_analysis_category,
  exam_analysis_trimester,
  exam_analysis_children_range,
  exam_analysis_status,
  formatted_exam_analysis_category,
  formatted_exam_analysis_trimester,
  formatted_exam_analysis_children_range,
  trimesterFromWeeks
} from '@/entities/exam-analysis'

// utils
import { v4 as uuidv4 } from 'uuid'
import { format, parse, isValid } from 'date-fns'

const loc = '@/pages/ExamAnalysisForm'

function formatDateBR(iso?: string | null): string {
  if (!iso) return '—'
  const d = parse(iso, 'yyyy-MM-dd', new Date())
  return isValid(d) ? format(d, 'dd/MM/yyyy') : '—'
}

export default function ExamAnalysisForm() {
  const navigate = useNavigate()
  const { ref } = useParams<{ ref?: string }>()
  const { toast } = useToast()

  const items = useExamAnalysisStore(s => s.items)
  const fetchByRef = useExamAnalysisStore(s => s.fetchByRef)
  const createRec = useExamAnalysisStore(s => s.create)
  const updateRec = useExamAnalysisStore(s => s.update)
  const generateAnalysisFn = useExamAnalysisStore(s => s.generateAnalysis)

  const [existing, setExisting] = useState(() => (ref ? items.find(i => i.ref === ref) : null) || null)
  const isNew = !existing

  const [collections, setCollections] = useState<ExamCollection[]>(
    existing?.collections && existing.collections.length > 0
      ? existing.collections
      : [{ ref: uuidv4(), date: null }]
  )
  const [markerValues, setMarkerValues] = useState<ExamMarkerValues>(existing?.markerValues ?? {})
  const [showResult, setShowResult] = useState(false)
  const [savedRecordRef, setSavedRecordRef] = useState<string | null>(existing?.ref ?? null)
  const dirtyRef = useRef(false)
  const lastAppliedCategoryRef = useRef<exam_analysis_category | null>(existing?.category ?? null)

  const form = useForm<ExamAnalysisFormSchema>({
    resolver: zodResolver(examAnalysisFormSchema),
    defaultValues: {
      patientName: existing?.patientName || '',
      category: existing?.category || exam_analysis_category.GESTANTE,
      trimester: existing?.trimester ?? exam_analysis_trimester.TRIM1,
      childrenRange: existing?.childrenRange ?? null,
      gestationalWeeks: existing?.gestationalWeeks ?? undefined,
      gestationalDays: existing?.gestationalDays ?? undefined
    }
  })

  const formValues = useWatch({ control: form.control })
  const category = (formValues.category as exam_analysis_category) || exam_analysis_category.GESTANTE
  const isGestante = category === exam_analysis_category.GESTANTE
  const isCrianca = category === exam_analysis_category.CRIANCA

  const markersList = useMemo(() => getMarkersForCategory(category), [category])
  const rangeKey = useMemo(
    () =>
      resolveRangeKey({
        category,
        trimester: (formValues.trimester as exam_analysis_trimester | null) ?? null,
        childrenRange: (formValues.childrenRange as exam_analysis_children_range | null) ?? null
      }),
    [category, formValues.trimester, formValues.childrenRange]
  )

  // Coleta atual = última da lista
  const currentCollection = collections[collections.length - 1]
  const statsCurrent = useMemo(
    () => computeMarkerStats(markerValues, markersList, rangeKey, currentCollection?.ref),
    [markerValues, markersList, rangeKey, currentCollection?.ref]
  )
  const statsTotal = useMemo(
    () => computeMarkerStats(markerValues, markersList, rangeKey),
    [markerValues, markersList, rangeKey]
  )

  // IG informada manualmente (gestante)
  const igWeeks = isGestante ? (formValues.gestationalWeeks as number | undefined) : undefined
  const igDays = isGestante ? (formValues.gestationalDays as number | undefined) : undefined
  const igLabelText = igWeeks !== undefined && igWeeks !== null ? `${igWeeks}s ${igDays ?? 0}d` : '—'

  // Sincroniza trimestre automaticamente quando semanas são informadas
  useEffect(() => {
    if (!isGestante) return
    const inferred = trimesterFromWeeks(igWeeks)
    if (inferred && formValues.trimester !== inferred) {
      form.setValue('trimester', inferred)
    }
  }, [igWeeks, isGestante])

  // Carrega remoto se não tem em cache
  useEffect(() => {
    if (!ref || existing) return
    fetchByRef(ref).then(record => {
      if (record) {
        setExisting(record)
        setSavedRecordRef(record.ref)
        setCollections(record.collections && record.collections.length > 0 ? record.collections : [{ ref: uuidv4(), date: null }])
        setMarkerValues(record.markerValues ?? {})
        form.reset({
          patientName: record.patientName,
          category: record.category,
          trimester: record.trimester ?? exam_analysis_trimester.TRIM1,
          childrenRange: record.childrenRange ?? null,
          gestationalWeeks: record.gestationalWeeks ?? undefined,
          gestationalDays: record.gestationalDays ?? undefined
        })
      }
    })
  }, [ref])

  // Detecta dirty
  useEffect(() => {
    const initial = JSON.stringify({
      patientName: existing?.patientName || '',
      category: existing?.category || exam_analysis_category.GESTANTE,
      trimester: existing?.trimester ?? exam_analysis_trimester.TRIM1,
      childrenRange: existing?.childrenRange ?? null,
      gestationalWeeks: existing?.gestationalWeeks ?? null,
      gestationalDays: existing?.gestationalDays ?? null,
      collections: existing?.collections ?? [{ ref: '__init', date: null }],
      markerValues: existing?.markerValues ?? {}
    })
    const current = JSON.stringify({
      patientName: formValues.patientName || '',
      category: formValues.category || exam_analysis_category.GESTANTE,
      trimester: formValues.trimester ?? null,
      childrenRange: formValues.childrenRange ?? null,
      gestationalWeeks: formValues.gestationalWeeks ?? null,
      gestationalDays: formValues.gestationalDays ?? null,
      collections: collections.map(c => ({ ref: c.ref, date: c.date })),
      markerValues
    })
    dirtyRef.current = initial !== current
  }, [formValues, collections, markerValues, existing])

  // Limpa trimester/childrenRange quando não aplica
  useEffect(() => {
    if (!isGestante) {
      form.setValue('trimester', null as never)
      form.setValue('gestationalWeeks', undefined as never)
      form.setValue('gestationalDays', undefined as never)
    } else if (!form.getValues('trimester')) {
      form.setValue('trimester', exam_analysis_trimester.TRIM1)
    }
    if (!isCrianca) {
      form.setValue('childrenRange', null as never)
    } else if (!form.getValues('childrenRange')) {
      form.setValue('childrenRange', exam_analysis_children_range.R_0_6M)
    }
  }, [isGestante, isCrianca])

  const onCategoryChange = async (next: exam_analysis_category) => {
    const previous = (lastAppliedCategoryRef.current ?? form.getValues('category')) as exam_analysis_category
    if (next === previous) {
      form.setValue('category', next)
      return
    }
    const hasFilled = statsTotal.filled > 0
    if (hasFilled) {
      const { isConfirmed } = await showConfirm('A mudança de categoria irá remover os valores já preenchidos da tabela de marcadores. Deseja continuar?')
      if (!isConfirmed) return
      setMarkerValues({})
    }
    lastAppliedCategoryRef.current = next
    form.setValue('category', next)
  }

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      if (!dirtyRef.current) return
      const target = e.target as HTMLElement | null
      if (!target) return
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('http') || href.startsWith('#')) return
      if (anchor.closest('[data-exam-form="container"]')) return
      e.preventDefault()
      e.stopPropagation()
      ;(async () => {
        const { isConfirmed } = await showConfirm('Existem alterações não salvas. Deseja sair sem salvar?')
        if (isConfirmed) navigate(href)
      })()
    }
    document.addEventListener('click', onClickCapture, true)
    return () => document.removeEventListener('click', onClickCapture, true)
  }, [navigate])

  const navigateGuarded = async (to: string) => {
    if (!dirtyRef.current) {
      navigate(to)
      return
    }
    const { isConfirmed } = await showConfirm('Existem alterações não salvas. Deseja sair sem salvar?')
    if (isConfirmed) navigate(to)
  }

  /**
   * Valida a tabela de coletas (estado fora do react-hook-form).
   * Exige ao menos 1 coleta e que toda coleta com algum valor preenchido
   * tenha data definida.
   */
  const validateCollections = (): string | null => {
    if (!collections.length) return 'Adicione pelo menos uma coleta.'
    for (let i = 0; i < collections.length; i++) {
      const c = collections[i]
      const hasAnyValue = Object.values(markerValues).some(byCol => {
        const v = byCol[c.ref]
        return v !== undefined && v !== null && String(v).trim() !== ''
      })
      if (!c.date) {
        // Sem data: aceita se a coleta também não tem valores (linha em branco)
        if (hasAnyValue) return `Informe a data da coleta ${i + 1} (há valores preenchidos sem data).`
      }
    }
    return null
  }

  const persistRecord = async (): Promise<string | null> => {
    const payload = {
      patientName: form.getValues('patientName'),
      category: form.getValues('category'),
      trimester: isGestante ? (form.getValues('trimester') ?? null) : null,
      childrenRange: isCrianca ? (form.getValues('childrenRange') ?? null) : null,
      gestationalWeeks: isGestante ? (form.getValues('gestationalWeeks') ?? null) : null,
      gestationalDays: isGestante ? (form.getValues('gestationalDays') ?? null) : null,
      collections,
      markerValues,
      // Metadados ricos: backend usa pra enviar nomes + faixa + classificação à IA.
      markersContext: buildMarkersContext(markersList, rangeKey, markerValues)
    }
    if (savedRecordRef) {
      const updated = await updateRec(savedRecordRef, payload)
      if (!updated) return null
      setExisting(updated)
      dirtyRef.current = false
      return updated.ref
    }
    const created = await createRec(payload)
    if (!created) return null
    setExisting(created)
    setSavedRecordRef(created.ref)
    dirtyRef.current = false
    return created.ref
  }

  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const isBusy = isSaving || isGenerating

  const onSave = form.handleSubmit(async values => {
    const fullLoc = `${loc}.onSave`
    if (isBusy) return
    try {
      const crossErr = validateExamAnalysisCrossFields(values)
      if (crossErr) {
        toast({ variant: 'destructive', title: 'Atenção', description: crossErr })
        return
      }
      const collectionsError = validateCollections()
      if (collectionsError) {
        toast({ variant: 'destructive', title: 'Atenção', description: collectionsError })
        return
      }
      const { isConfirmed } = await showConfirm(
        isNew ? `Salvar a análise de exames de ${values.patientName}?` : `Salvar alterações em ${values.patientName}?`
      )
      if (!isConfirmed) return
      setIsSaving(true)
      const newRef = await persistRecord()
      if (!newRef) {
        toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível salvar.' })
        return
      }
      toast({ variant: 'success', title: 'Sucesso', description: `Análise de ${values.patientName} salva.` })
      navigate('/exam-analysis')
    } catch (err) {
      console.error(`Unhandled rejection at ${fullLoc}.`, err)
      toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível salvar.' })
    } finally {
      setIsSaving(false)
    }
  })

  const onGenerate = async () => {
    const fullLoc = `${loc}.onGenerate`
    if (isBusy) return
    const isValidForm = await form.trigger()
    if (!isValidForm) return
    const values = form.getValues()
    const crossErr = validateExamAnalysisCrossFields(values)
    if (crossErr) {
      toast({ variant: 'destructive', title: 'Atenção', description: crossErr })
      return
    }
    const collectionsError = validateCollections()
    if (collectionsError) {
      toast({ variant: 'destructive', title: 'Atenção', description: collectionsError })
      return
    }
    if (statsTotal.filled === 0) {
      toast({ variant: 'destructive', title: 'Atenção', description: 'Preencha ao menos um marcador para gerar a análise.' })
      return
    }
    const { isConfirmed } = await showConfirm('Gerar análise com a Nina? O registro será salvo automaticamente e a análise será processada em segundo plano.')
    if (!isConfirmed) return
    try {
      setIsGenerating(true)
      const newRef = await persistRecord()
      if (!newRef) {
        toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível salvar antes de gerar a análise.' })
        return
      }
      const result = await generateAnalysisFn(newRef)
      if (result && 'error' in result) {
        toast({ variant: 'destructive', title: 'Ops', description: result.error })
        return
      }
      toast({ variant: 'success', title: 'Análise enfileirada', description: 'Você será notificado quando a Nina concluir.' })
      navigate('/exam-analysis')
    } catch (err) {
      console.error(`Unhandled rejection at ${fullLoc}.`, err)
      toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível gerar a análise.' })
    } finally {
      setIsGenerating(false)
    }
  }

  const onClearAll = async () => {
    try {
      const { isConfirmed } = await showConfirm('Deseja limpar todos os valores preenchidos? Os dados da paciente e as coletas serão preservados.')
      if (!isConfirmed) return
      setMarkerValues({})
    } catch (err) {
      console.error(`Unhandled rejection at ${loc}.onClearAll.`, err)
    }
  }

  const onContextChange = async (
    name: 'trimester' | 'childrenRange',
    next: exam_analysis_trimester | exam_analysis_children_range
  ) => {
    if (statsTotal.filled > 0) {
      const label = name === 'trimester' ? 'trimestre' : 'faixa etária'
      const { isConfirmed } = await showConfirm(`Mudar o ${label} pode tornar a classificação dos marcadores inconsistente. As cores serão recalculadas. Deseja continuar?`)
      if (!isConfirmed) return
    }
    form.setValue(name, next as never)
  }

  const onAddCollection = () => {
    setCollections([...collections, { ref: uuidv4(), date: null }])
  }

  const onRemoveCollection = async (refId: string) => {
    if (collections.length <= 1) return
    const hasValuesInThisCollection = Object.values(markerValues).some(byCol => {
      const v = byCol[refId]
      return v !== undefined && v !== null && String(v).trim() !== ''
    })
    const message = hasValuesInThisCollection
      ? 'Esta coleta possui valores preenchidos. Removê-la apagará esses valores. Deseja continuar?'
      : 'Remover esta coleta? Esta ação não pode ser desfeita.'
    const { isConfirmed } = await showConfirm(message)
    if (!isConfirmed) return
    setCollections(collections.filter(c => c.ref !== refId))
    // remove valores apontando para essa coleta
    const cleaned: ExamMarkerValues = {}
    for (const [mid, byCol] of Object.entries(markerValues)) {
      const next: Record<string, string> = {}
      for (const [colRef, val] of Object.entries(byCol)) {
        if (colRef !== refId) next[colRef] = val
      }
      if (Object.keys(next).length > 0) cleaned[mid] = next
    }
    setMarkerValues(cleaned)
  }

  const onDownloadPdf = () => {
    toast({ variant: 'default', title: 'Em construção', description: 'O download em PDF estará disponível após a integração com a Nina.' })
  }

  const persistedRecord = useExamAnalysisStore(s => (savedRecordRef ? s.items.find(i => i.ref === savedRecordRef) : null))
  const previewRecord = persistedRecord && persistedRecord.status === exam_analysis_status.ACTIVE ? persistedRecord : null

  // Cards no topo — adaptados por categoria
  const contextLabel = isGestante
    ? formValues.trimester
      ? formatted_exam_analysis_trimester[formValues.trimester as exam_analysis_trimester]
      : '—'
    : isCrianca
      ? formValues.childrenRange
        ? formatted_exam_analysis_children_range[formValues.childrenRange as exam_analysis_children_range]
        : '—'
      : formatted_exam_analysis_category[category]

  return (
    <>
      <PageHeader
        title={isNew ? 'Nova análise de exames' : `Editar — ${existing?.patientName}`}
        description="Preencha os marcadores laboratoriais da paciente para gerar uma análise clínica com apoio da Nina"
      />

      <Form {...form}>
        <form onSubmit={onSave} className="mt-4 space-y-5 w-full" data-exam-form="container">
          {/* Identificação */}
          <div className="rounded-2xl border border-border bg-card shadow-soft p-5 mdAndUp:p-6 w-full">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-display text-lg font-semibold text-foreground">Identificação da paciente</h2>
            </div>
            <div
              className={cnGrid(isGestante, isCrianca)}
            >
              <FormField
                control={form.control}
                name="patientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Nome da paciente *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Digite o nome completo" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Categoria *</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={v => onCategoryChange(v as exam_analysis_category)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(exam_analysis_category).map(c => (
                            <SelectItem key={c} value={c}>
                              {formatted_exam_analysis_category[c]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isGestante && (
                <FormField
                  control={form.control}
                  name="trimester"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">Trimestre *</FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string | undefined) ?? ''}
                          onValueChange={v => onContextChange('trimester', v as exam_analysis_trimester)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(exam_analysis_trimester).map(t => (
                              <SelectItem key={t} value={t}>
                                {formatted_exam_analysis_trimester[t]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isGestante && (
                <FormItem>
                  <FormLabel className="text-foreground font-medium">Idade gestacional</FormLabel>
                  <div className="flex items-center gap-2">
                    <FormField
                      control={form.control}
                      name="gestationalWeeks"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <div className="relative">
                              <Input
                                type="number"
                                min={0}
                                max={42}
                                inputMode="numeric"
                                placeholder="0"
                                value={typeof field.value === 'number' ? field.value : (field.value ?? '')}
                                onChange={e => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                                className="pr-12"
                                aria-label="Semanas"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">sem</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="gestationalDays"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <div className="relative">
                              <Input
                                type="number"
                                min={0}
                                max={6}
                                inputMode="numeric"
                                placeholder="0"
                                value={typeof field.value === 'number' ? field.value : (field.value ?? '')}
                                onChange={e => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                                className="pr-12"
                                aria-label="Dias"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">dias</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </FormItem>
              )}

              {isCrianca && (
                <FormField
                  control={form.control}
                  name="childrenRange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">Faixa etária *</FormLabel>
                      <FormControl>
                        <Select
                          value={(field.value as string | undefined) ?? ''}
                          onValueChange={v => onContextChange('childrenRange', v as exam_analysis_children_range)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(exam_analysis_children_range).map(c => (
                              <SelectItem key={c} value={c}>
                                {formatted_exam_analysis_children_range[c]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>

          {/* Cards-resumo no topo da tabela — 4 cards em uma linha no desktop */}
          <div className="grid grid-cols-1 mdAndUp:grid-cols-2 lgAndUp:grid-cols-4 gap-3 w-full">
            <SummaryCard icon={<Activity className="h-4 w-4" />} label="Contexto" value={contextLabel} sub={formatted_exam_analysis_category[category]} />
            {isGestante ? (
              <SummaryCard
                icon={<Calendar className="h-4 w-4" />}
                label="IG atual"
                value={igLabelText}
                sub={formValues.trimester ? formatted_exam_analysis_trimester[formValues.trimester as exam_analysis_trimester] : 'Informe semanas e dias'}
              />
            ) : (
              <SummaryCard icon={<Calendar className="h-4 w-4" />} label="Coleta atual" value={formatDateBR(currentCollection?.date)} sub={`${collections.length} coleta${collections.length !== 1 ? 's' : ''} registrada${collections.length !== 1 ? 's' : ''}`} />
            )}
            <SummaryCard icon={<ListChecks className="h-4 w-4" />} label="Preenchidos" value={`${statsCurrent.filled}/${markersList.length}`} sub="Na coleta atual" />
            <SummaryCard icon={<AlertTriangle className="h-4 w-4" />} label="Fora do ideal" value={String(statsCurrent.outOfRange)} sub={statsCurrent.attention > 0 ? `+ ${statsCurrent.attention} em atenção` : 'Na coleta atual'} tone={statsCurrent.outOfRange > 0 ? 'danger' : 'default'} />
          </div>

          {/* Tabela de marcadores */}
          <div className="rounded-2xl border border-border bg-card shadow-soft p-5 mdAndUp:p-6">
            <div className="flex items-center justify-between gap-4 mb-4 smAndDown:flex-col smAndDown:items-start">
              <div>
                <h2 className="font-display text-lg font-semibold text-foreground">Marcadores laboratoriais</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Valores de referência da metodologia Mãe Nutrida para {formatted_exam_analysis_category[category]}
                  {isGestante && formValues.trimester ? ` · ${formatted_exam_analysis_trimester[formValues.trimester as exam_analysis_trimester]}` : ''}
                  {isCrianca && formValues.childrenRange ? ` · ${formatted_exam_analysis_children_range[formValues.childrenRange as exam_analysis_children_range]}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 font-semibold">OK: {statsCurrent.ok}</span>
                <span className="px-2 py-1 rounded-md bg-amber-100 text-amber-700 font-semibold">Atenção: {statsCurrent.attention}</span>
                <span className="px-2 py-1 rounded-md bg-red-100 text-red-700 font-semibold">Fora: {statsCurrent.outOfRange}</span>
              </div>
            </div>

            <MarkersTable
              category={category}
              markers={markersList}
              rangeKey={rangeKey}
              collections={collections}
              values={markerValues}
              onValuesChange={setMarkerValues}
              onCollectionsChange={setCollections}
              onAddCollection={onAddCollection}
              onRemoveCollection={onRemoveCollection}
            />
          </div>

          {/* Footer fixo de ações */}
          <div className="sticky bottom-0 -mx-3 mdAndUp:-mx-6 lgAndUp:-mx-12 px-3 mdAndUp:px-6 lgAndUp:px-12 py-3 mt-6 backdrop-blur-md bg-card/80 border-t border-border z-20">
            <div className="flex items-center justify-between gap-3 smAndDown:flex-col">
              <div className="flex items-center gap-2 text-sm text-muted-foreground smAndDown:w-full smAndDown:justify-center">
                <span>
                  <strong className="text-coentro font-semibold">{statsTotal.filled}</strong>/{statsTotal.total} marcador{statsTotal.total !== 1 ? 'es' : ''} preenchido{statsTotal.filled !== 1 ? 's' : ''} · <strong>{collections.length}</strong> coleta{collections.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2 smAndDown:w-full smAndDown:flex-col">
                <Button type="button" variant="outline" size="sm" onClick={() => navigateGuarded('/exam-analysis')} className="gap-2 smAndDown:w-full" disabled={isBusy}>
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </Button>
                {statsTotal.filled > 0 && (
                  <Button type="button" variant="destructive" size="sm" onClick={onClearAll} className="gap-2 smAndDown:w-full" disabled={isBusy}>
                    <Trash2 className="h-4 w-4" />
                    Limpar
                  </Button>
                )}
                <Button type="submit" variant="default" size="sm" className="gap-2 font-semibold smAndDown:w-full" disabled={isBusy}>
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" size="sm" onClick={onGenerate} className="gap-2 font-semibold smAndDown:w-full bg-coentro text-white hover:bg-coentro/90" disabled={statsTotal.filled === 0 || isBusy}>
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {isGenerating ? 'Gerando...' : 'Gerar análise'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>

      <ExamAnalysisResultDialog
        open={showResult}
        record={previewRecord}
        onClose={() => setShowResult(false)}
        onDownloadPdf={onDownloadPdf}
      />
    </>
  )
}

function cnGrid(isGestante: boolean, isCrianca: boolean): string {
  // Mantém classes estáticas — Tailwind não suporta interpolação dinâmica
  if (isGestante) return 'grid grid-cols-1 gap-4 mdAndUp:grid-cols-2 lgAndUp:grid-cols-4'
  if (isCrianca) return 'grid grid-cols-1 gap-4 mdAndUp:grid-cols-2 lgAndUp:grid-cols-3'
  return 'grid grid-cols-1 gap-4 mdAndUp:grid-cols-2'
}

interface SummaryCardProps {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  tone?: 'default' | 'danger'
}
function SummaryCard({ icon, label, value, sub, tone = 'default' }: SummaryCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-soft px-4 py-3 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-coentro">{icon}</span>
        {label}
      </div>
      <div className={`text-xl font-semibold leading-tight ${tone === 'danger' ? 'text-red-700' : 'text-foreground'}`}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  )
}
